// components/messagerie/MessageriePage.js
import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import './MessageriePage.css';

// Simulation de conversations initiales (remplacées par l'API en production)
const DEMO_CONVERSATIONS = [
  {
    id: 1,
    contact: { nom: 'Karim B.', initiales: 'KB', annonce: 'Villa F5 Hydra' },
    messages: [
      { id: 1, texte: 'Bonjour, est-ce que la villa est toujours disponible ?', moi: false, date: '2026-03-24T10:15:00' },
      { id: 2, texte: 'Oui elle est disponible. Vous souhaitez visiter ?', moi: true, date: '2026-03-24T10:30:00' },
      { id: 3, texte: 'Oui avec plaisir, je suis disponible ce weekend.', moi: false, date: '2026-03-24T11:02:00' },
    ],
    nonLus: 1,
  },
  {
    id: 2,
    contact: { nom: 'Sarah M.', initiales: 'SM', annonce: 'Appartement F3 Bab Ezzouar' },
    messages: [
      { id: 1, texte: 'Le prix est-il négociable ?', moi: false, date: '2026-03-23T15:00:00' },
      { id: 2, texte: 'Il y a une légère marge oui, appelez-moi pour en discuter.', moi: true, date: '2026-03-23T15:45:00' },
    ],
    nonLus: 0,
  },
];

function formatHeure(iso, yesterday) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return yesterday;
  if (diffDays < 7) return d.toLocaleDateString('fr-FR', { weekday: 'short' });
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function MessageriePage() {
  const { t } = useI18n();
  const [convs, setConvs] = useState(() => {
    try {
      const saved = localStorage.getItem('darna-immo_messages');
      return saved ? JSON.parse(saved) : DEMO_CONVERSATIONS;
    } catch { return DEMO_CONVERSATIONS; }
  });
  const [activeId, setActiveId] = useState(convs[0]?.id || null);
  const [texte, setTexte] = useState('');
  const messagesListRef = useRef(null);
  const textareaRef = useRef(null);

  const activeConv = convs.find(c => c.id === activeId);

  // Scroll DANS le container messages, pas dans la page
  useEffect(() => {
    const el = messagesListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeId, convs]);

  const persist = (updated) => {
    setConvs(updated);
    try { localStorage.setItem('darna-immo_messages', JSON.stringify(updated)); } catch {}
  };

  const openConv = (id) => {
    setActiveId(id);
    // Marquer comme lus
    persist(convs.map(c => c.id === id ? { ...c, nonLus: 0 } : c));
  };

  const envoyerMessage = (e) => {
    e.preventDefault();
    if (!texte.trim() || !activeConv) return;
    const newMsg = {
      id: Date.now(),
      texte: texte.trim(),
      moi: true,
      date: new Date().toISOString(),
    };
    persist(convs.map(c =>
      c.id === activeId ? { ...c, messages: [...c.messages, newMsg] } : c
    ));
    setTexte('');
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      envoyerMessage(e);
    }
  };

  const totalNonLus = convs.reduce((s, c) => s + c.nonLus, 0);

  return (
    <div className="messagerie-page">
      <div className="messagerie-header">
        <h2>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          {t.messages.title}
          {totalNonLus > 0 && <span className="msg-total-badge">{totalNonLus}</span>}
        </h2>
        <p>{t.messages.exchanges}</p>
      </div>

      <div className="messagerie-layout">
        {/* ── Liste des conversations ── */}
        <div className="conv-list">
          {convs.map(conv => {
            const last = conv.messages[conv.messages.length - 1];
            return (
              <div
                key={conv.id}
                className={`conv-item ${conv.id === activeId ? 'active' : ''} ${conv.nonLus > 0 ? 'unread' : ''}`}
                onClick={() => openConv(conv.id)}
              >
                <div className="conv-avatar">{conv.contact.initiales}</div>
                <div className="conv-info">
                  <div className="conv-name-row">
                    <span className="conv-name">{conv.contact.nom}</span>
                    <span className="conv-time">{last ? formatHeure(last.date, t.messages.yesterday) : ''}</span>
                  </div>
                  <span className="conv-annonce">{conv.contact.annonce}</span>
                  <p className="conv-preview">
                    {last ? (last.moi ? `${t.messages.you} : ` : '') + last.texte.substring(0, 55) + (last.texte.length > 55 ? '…' : '') : ''}
                  </p>
                </div>
                {conv.nonLus > 0 && (
                  <span className="conv-badge">{conv.nonLus}</span>
                )}
              </div>
            );
          })}

          {convs.length === 0 && (
            <div className="conv-empty">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              <p>{t.messages.noConv}</p>
            </div>
          )}
        </div>

        {/* ── Fil de messages ── */}
        <div className="conv-detail">
          {activeConv ? (
            <>
              <div className="conv-detail-header">
                <div className="conv-detail-avatar">{activeConv.contact.initiales}</div>
                <div>
                  <span className="conv-detail-name">{activeConv.contact.nom}</span>
                  <span className="conv-detail-annonce">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    </svg>
                    {activeConv.contact.annonce}
                  </span>
                </div>
              </div>

              <div className="messages-list" ref={messagesListRef}>
                {activeConv.messages.map(msg => (
                  <div key={msg.id} className={`message-bubble ${msg.moi ? 'moi' : 'eux'}`}>
                    <p>{msg.texte}</p>
                    <span className="message-time">{formatHeure(msg.date)}</span>
                  </div>
                ))}
              </div>

              <form className="message-input-row" onSubmit={envoyerMessage}>
                <textarea
                  ref={textareaRef}
                  rows="1"
                  placeholder={t.messages.placeholder}
                  value={texte}
                  onChange={e => setTexte(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button type="submit" disabled={!texte.trim()} title={t.messages.send}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </form>
            </>
          ) : (
            <div className="conv-placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              <p>{t.messages.selectConv}</p>
            </div>
          )}
        </div>
      </div>

      <div className="messagerie-disclaimer">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <polyline points="9 12 11 14 15 10"/>
        </svg>
        <span>{t.messages.disclaimer}</span>
      </div>
    </div>
  );
}

export default MessageriePage;
